import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Select, Space, Switch, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  ASSET_CLASSES,
  ASSET_CLASS_PERCENT,
  AssetClassOptions,
  FX,
  PER_ASSET_NOTIONAL,
  RISK_WEIGHTED_APPROACH,
  selectors,
  updateAssetClassOptions,
  updateHardSoftLimits,
  updateMetricLimits,
  updateMRR,
} from 'state';
import { formatters } from 'services';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import { isSessionLocked } from '../../SessionSettingsShared/helpers';
import InlineNumber from './InlineNumber';
import { InstrumentSelectionContext } from '../../SessionSettings/instrumentSelectionContext';
import './inline_edit.css';

/**
 * Seamless v2 of Limits & Risk (LimitsRiskPanel). Same selectors, dispatches,
 * payloads, percent-fraction (0–1) conventions and lock semantics — but with NO
 * edit mode. Every control is always mounted: numeric cells commit on blur/Enter
 * and the Hard/Soft switch applies instantly, so nothing ever swaps in/out and
 * the cards never reflow. Returns a <> fragment of up to three stacked cards.
 */

type LimitsState = {
  [assetClass: number]: AssetClassOptions;
};

/* Percent fraction (0–1) editor conventions, copied verbatim from the v1
   panel so on-screen behaviour is identical. */
const pctFormatter = (value: string | number | undefined) => {
  const nr = Number(value) || 0;
  return `${Math.round(nr * 100)}%`;
};
const pctParser = (value: string | undefined) => {
  const parsedValue = `${value?.replaceAll('%', '')}`;
  const nr = Number(parsedValue);
  if (!isNaN(nr)) {
    return Number((nr / 100).toFixed(2));
  }
  return 0;
};

const HARD_SOFT_INFO =
  'Hard limits impose a strict cap on player trading ability once limit is reached, while soft limits can be surpassed.';

/* ---- Card 1: Instrument Limits per Asset Class -------------------------- */
const InstrumentLimitsCard: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const { asset_class_options, risk_approach, id, hard_limits } = session;
  const locked = isSessionLocked(session);
  const instrumentSelection = useContext(InstrumentSelectionContext);
  const limitMode =
    instrumentSelection?.limitMode ??
    session.instrument_limit_mode ??
    ASSET_CLASS_PERCENT;

  const buildLimits = (): LimitsState => {
    const next = {} as LimitsState;
    asset_class_options.forEach(a => {
      next[a.asset_class] = {
        asset_class: a.asset_class,
        pv_limit: a.pv_limit,
        risk_weight: a.risk_weight,
      };
    });
    return next;
  };
  // Initialise from the session so the first paint shows real values (no flash).
  const [limits, setLimits] = useState<LimitsState>(buildLimits);
  const playableAssetClasses_ = useSelector(selectors.playableAssetClasses());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setLimits(buildLimits()), [asset_class_options]);

  // Same row inclusion as v1: playable, non-FX asset classes, in store order.
  const baseRows = asset_class_options
    .map(item => ({
      name: ASSET_CLASSES[item.asset_class],
      assetClass: item.asset_class,
    }))
    .filter(r => r.assetClass !== FX);
  const eligibilityByAsset = new Map(
    (session.notional_limit_eligibility ?? []).map(row => [
      row.underlying_asset,
      row.eligible,
    ]),
  );
  const excludedSelectedAssetClasses = new Set(
    (instrumentSelection?.selected ?? session.underlying_assets ?? [])
      .filter(assetId => eligibilityByAsset.get(assetId) === false)
      .map(
        assetId =>
          session.notional_limit_eligibility?.find(
            row => row.underlying_asset === assetId,
          )?.asset_class,
      )
      .filter(
        (assetClass): assetClass is number => typeof assetClass === 'number',
      ),
  );
  const rows =
    limitMode === PER_ASSET_NOTIONAL
      ? baseRows.filter(row => excludedSelectedAssetClasses.has(row.assetClass))
      : baseRows.filter(row => playableAssetClasses_.includes(row.assetClass));

  const showRiskWeight =
    limitMode === ASSET_CLASS_PERCENT &&
    risk_approach === RISK_WEIGHTED_APPROACH;

  // Optimistic: update local then dispatch the FULL set, exactly as v1 did on
  // Save (identical action + payload shape).
  const commitField = (
    assetClass: number,
    field: 'pv_limit' | 'risk_weight',
    value: number,
  ) => {
    const cur = limits[assetClass];
    const next: LimitsState = {
      ...limits,
      [assetClass]: {
        ...cur,
        asset_class: assetClass,
        [field]: value,
      },
    };
    setLimits(next);
    dispatch(updateAssetClassOptions({ asset_class_options: next }));
  };

  const assetClassLimitsInfo = (
    <Tooltip
      title={
        limitMode === PER_ASSET_NOTIONAL
          ? 'V2 caps each eligible instrument at its own current reporting-currency market value.'
          : `Specifying a limit on a certain asset class will enforce a ${
              hard_limits ? 'hard' : 'soft'
            } limit on each instrument of that asset class.`
      }
    >
      <InfoCircleOutlined style={{ marginLeft: 6 }} />
    </Tooltip>
  );

  // Instant hard/soft toggle. Hovering it explains hard vs soft; when the
  // session is locked the tooltip also notes it can no longer be changed.
  const modeSelector = (
    <Select
      size="small"
      value={limitMode}
      disabled={
        locked ||
        !instrumentSelection?.setLimitMode ||
        (instrumentSelection.autoSaveDraft && !instrumentSelection.draftReady)
      }
      style={{ width: 190 }}
      onChange={(value: 0 | 1) => instrumentSelection?.setLimitMode?.(value)}
    >
      <Select.Option value={ASSET_CLASS_PERCENT}>
        Per asset class (V1)
      </Select.Option>
      <Select.Option
        value={PER_ASSET_NOTIONAL}
        disabled={risk_approach === RISK_WEIGHTED_APPROACH}
        title="V2 requires the cash risk approach."
      >
        Per instrument notional (V2)
      </Select.Option>
    </Select>
  );
  const hardSoftToggle = (
    <Tooltip
      title={
        locked
          ? `${HARD_SOFT_INFO} Cannot modify hard/soft limits after the session has been started.`
          : HARD_SOFT_INFO
      }
    >
      <span>
        <Switch
          size="small"
          checked={hard_limits}
          disabled={locked}
          onChange={() => {
            dispatch(
              updateHardSoftLimits({
                session_id: id,
                hard_limits: !hard_limits,
              }),
            );
          }}
          checkedChildren="Hard"
          unCheckedChildren="Soft"
        />
      </span>
    </Tooltip>
  );
  const extra = (
    <Space size={8}>
      {modeSelector}
      {limitMode === ASSET_CLASS_PERCENT ? hardSoftToggle : null}
    </Space>
  );

  const titleNode = <>Instrument Limits{assetClassLimitsInfo}</>;

  const limitsTable = (title: React.ReactNode, cardExtra?: React.ReactNode) => (
    <SettingsCard
      title={title}
      extra={cardExtra}
      className="set3b-fill-table set3b-instr-scroll"
    >
      <div className="ie-scroll">
        <table className="ie-table">
          <colgroup>
            <col style={{ width: showRiskWeight ? '50%' : '60%' }} />
            <col style={{ width: showRiskWeight ? '25%' : '40%' }} />
            {showRiskWeight ? <col style={{ width: '25%' }} /> : null}
          </colgroup>
          <thead>
            <tr>
              <th>Asset Class</th>
              <th className="ie-col-num">Limit</th>
              {showRiskWeight ? (
                <th className="ie-col-num">Risk weight</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const obj = limits[r.assetClass];
              return (
                <tr key={r.assetClass}>
                  <td className="ie-row-label">{r.name}</td>
                  <td className="ie-col-num">
                    <InlineNumber
                      value={obj?.pv_limit ?? 0}
                      onCommit={v => commitField(r.assetClass, 'pv_limit', v)}
                      formatter={pctFormatter}
                      parser={pctParser}
                      min={0}
                      max={1}
                      step={0.01}
                      ariaLabel={`${r.name} limit`}
                    />
                  </td>
                  {showRiskWeight ? (
                    <td className="ie-col-num">
                      <InlineNumber
                        value={obj?.risk_weight ?? 0}
                        onCommit={v =>
                          commitField(r.assetClass, 'risk_weight', v)
                        }
                        formatter={pctFormatter}
                        parser={pctParser}
                        min={0}
                        max={1}
                        step={0.01}
                        ariaLabel={`${r.name} risk weight`}
                      />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SettingsCard>
  );

  if (limitMode === PER_ASSET_NOTIONAL) {
    return (
      <>
        <SettingsCard title={titleNode} extra={modeSelector}>
          <Space direction="vertical" size={4}>
            <Typography.Text>
              Each eligible instrument has its own absolute cap in the session
              reporting currency. Margin remains a separate funding constraint.
            </Typography.Text>
            <Typography.Text type="secondary">
              Enter the values beside the selected instruments in Instrument
              Universe, then save the universe and all caps atomically.
            </Typography.Text>
            <Typography.Text type="secondary">
              Also select and cap each spot-FX pair required by a selected
              foreign-currency instrument; the backend will reject an incomplete
              universe.
            </Typography.Text>
            {session.instrument_limit_mode !== PER_ASSET_NOTIONAL ? (
              <Typography.Text type="warning">
                V2 is selected as a draft and is not active until that atomic
                save succeeds.
              </Typography.Text>
            ) : null}
          </Space>
        </SettingsCard>
        {rows.length
          ? limitsTable('Existing limits for excluded products', hardSoftToggle)
          : null}
      </>
    );
  }

  if (rows.length === 0) {
    return (
      <SettingsCard title={titleNode} extra={extra}>
        <Empty
          description={
            <Typography.Text type="secondary">
              Finish saving your instrument selection.
            </Typography.Text>
          }
        />
      </SettingsCard>
    );
  }

  return limitsTable(titleNode, extra);
};

/* ---- Card 2: Minimum Reserve Requirement (client_money only) ------------ */
const MinimumReserveCard: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const [mrr, setMrr] = useState<number>(session.mrr);

  useEffect(() => {
    setMrr(session.mrr);
  }, [session.mrr]);

  return (
    <SettingsCard title="Minimum Reserve Requirement">
      <div className="ie-formrow">
        <span className="ie-formrow__label">MRR</span>
        <span className="ie-formrow__control">
          <InlineNumber
            value={mrr}
            onCommit={value => {
              setMrr(value);
              dispatch(updateMRR({ session_id: session.id, mrr: value }));
            }}
            formatter={pctFormatter}
            parser={pctParser}
            min={0}
            max={1}
            step={0.01}
            ariaLabel="Minimum reserve requirement"
          />
        </span>
      </div>
    </SettingsCard>
  );
};

/* ---- Card 3: Metric Limits (legacy, limit_metric != null only) ---------- */
const MetricLimitsCard: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const [low, setLow] = useState<number>(session.metric_limit_low);
  const [high, setHigh] = useState<number>(session.metric_limit_high);

  useEffect(() => {
    setLow(session.metric_limit_low);
    setHigh(session.metric_limit_high);
  }, [session.metric_limit_low, session.metric_limit_high]);

  // Either commit dispatches the full {low_limit, high_limit} using the latest
  // local pair, same payload shape as v1.
  const commit = (nextLow: number, nextHigh: number) => {
    dispatch(updateMetricLimits({ low_limit: nextLow, high_limit: nextHigh }));
  };

  return (
    <SettingsCard
      title="Metric Limits (legacy)"
      extra={<Typography.Text type="secondary">Deprecated</Typography.Text>}
    >
      <div className="ie-formrow">
        <span className="ie-formrow__label">Low</span>
        <span className="ie-formrow__control">
          <InlineNumber
            value={low}
            onCommit={value => {
              setLow(value);
              commit(value, high);
            }}
            formatter={val => formatters.commasExact2Digits(val)}
            ariaLabel="Metric limit low"
          />
        </span>
      </div>
      <div className="ie-formrow">
        <span className="ie-formrow__label">High</span>
        <span className="ie-formrow__control">
          <InlineNumber
            value={high}
            onCommit={value => {
              setHigh(value);
              commit(low, value);
            }}
            formatter={val => formatters.commasExact2Digits(val)}
            ariaLabel="Metric limit high"
          />
        </span>
      </div>
    </SettingsCard>
  );
};

const LimitsCardV2: React.FC = () => {
  const session = useSelector(selectors.activeSession);
  const showMrr = session.client_money;
  const showMetric = session.limit_metric !== null;

  return (
    <>
      <InstrumentLimitsCard />
      {showMrr ? <MinimumReserveCard /> : null}
      {showMetric ? <MetricLimitsCard /> : null}
    </>
  );
};

export default LimitsCardV2;
